import React from 'react';
import { Separator } from '@/components/ui/separator';

interface TooltipRow {
  label: string;
  content: React.ReactNode;
  dataTestId?: string;
  id: string;
}

type GenericRangeTooltipContentProps = Readonly<{
  symbol: string | undefined;
  name: string;
  rows: TooltipRow[] | TooltipRow[][];
  footer?: React.ReactNode;
}>;

function isRowGroups(rows: TooltipRow[] | TooltipRow[][]): rows is TooltipRow[][] {
  return Array.isArray(rows[0]);
}

const Row = ({ data, appendSeparator = true }: { data: TooltipRow; appendSeparator?: boolean }) => {
  return (
    <>
      <div className="flex flex-row justify-between py-[4px]">
        <div className="whitespace-nowrap">{data.label}</div>
        <div data-testid={data.dataTestId} className="text-right">
          {data.content}
        </div>
      </div>
      {appendSeparator && <Separator className="bg-venn-grey-80" />}
    </>
  );
};

export const GenericRangeTooltipContent = ({ symbol, name, rows, footer }: GenericRangeTooltipContentProps) => {
  const isGrouped = isRowGroups(rows);

  return (
    <div className="flex flex-col w-[310px]">
      <div className="flex flex-row items-center gap-[4px]">
        {symbol && (
          <div className="text-[11px] font-bold py-[1px] px-[4px] rounded-[2px] bg-venn-grey-80 break-normal">
            {symbol}
          </div>
        )}
        <div className="text-[15px] font-bold leading-[1.3em]">{name}</div>
      </div>
      {rows.length > 0 && (
        <div className="flex flex-col text-[12px] mt-[8px]">
          {isGrouped
            ? rows.map((rowGroup, rowGroupIndex) =>
                rowGroup.map((row, rowIndex) => (
                  <Row
                    key={row.id}
                    data={row}
                    appendSeparator={rowIndex === rowGroup.length - 1 && rowGroupIndex !== rows.length - 1}
                  />
                )),
              )
            : rows.map((row, index) => <Row key={row.label} data={row} appendSeparator={index !== rows.length - 1} />)}
        </div>
      )}
      {footer}
    </div>
  );
};

export default GenericRangeTooltipContent;
